import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { apiFetch, ApiError } from '../lib/api'
import { isResolvableBreak } from '../lib/tasks'
import type { TaskQueueItem, ReconTaskData, BreakDiagnosis } from '../lib/tasks'
import { HelpDot } from './guidance/Guidance'
import Sentence from './Sentence'
import { getCurrentUser } from '../lib/auth'
import { verbRule } from '../lib/roleVerbs'

/* Reconcile pane — IO ledger screen `tasks_reconcile`.
 *
 * Ale ruling 2026-07-26. A break means the record's arithmetic doesn't tie
 * out, and the task now DIAGNOSES the likely cause by date: whichever side
 * is more recent is authoritative.
 *
 *   balance evidenced later than the last movement → the balance is current,
 *     so a MOVEMENT IS MISSING, for the delta;
 *   last movement later than the balance evidence → the movements are
 *     current, so the stored BALANCE IS STALE.
 *
 * The pane shows the suggestion WITH THE TWO DATES that produced it, so the
 * officer can check the reasoning rather than trust it, and either confirms
 * it or overrides it. Whatever they confirm is what gets written, under
 * their name (POST /tasks/{id}/resolve-break).
 *
 * When the engine cannot honestly decide — same-day dates, a missing date,
 * or a remedy that would contradict the record's own sign rules — it says
 * so, and both calls are offered with neither pre-selected. It never
 * asserts a cause it cannot support.
 *
 * The two NON-balance breaks (commitment_supply_mismatch,
 * position_leg_identity_violation) compare two stored figures. They carry a
 * not-applicable diagnosis and get their own honest card — previously they
 * fell through to a "Stale balance" card whose copy was simply wrong for
 * them. */

interface DrawerLite {
  title: string
  facts: Record<string, string | number | boolean>
}

const num = (v: unknown): number | null => {
  const n = parseFloat(String(v ?? ''))
  return Number.isFinite(n) ? n : null
}

function fmt(n: number | null): string {
  if (n == null) return '—'
  return n.toLocaleString('en-US', { maximumFractionDigits: 6 })
}

/** "unit" / "units" — "1 units unexplained" read as a typo on every
 *  single-unit break (atlas p4, 2026-07-28). */
function unitWord(n: number | null): string {
  return n != null && Math.abs(n) === 1 ? 'unit' : 'units'
}

function errText(e: unknown): string {
  if (e instanceof ApiError) {
    const d = (e.body as { detail?: unknown } | undefined)?.detail
    if (typeof d === 'string') return d
    return `${e.statusText} (${e.status})`
  }
  return e instanceof Error ? e.message : 'request failed'
}

/** Why the engine could not decide — officer language, no engine words. */
function undeterminableBecause(reason: string, balanceDate: string | null): string {
  switch (reason) {
    case 'balance_and_trade_same_date':
      return `The balance and the most recent movement carry the same date (${balanceDate}), so neither is clearly the later one.`
    case 'no_balance_evidence_date':
      return 'The balance carries no as-of date, so it cannot be dated against the movements.'
    case 'no_confirmed_trade_date':
      return 'There are no movements on record to date the balance against.'
    case 'no_balance_evidence_date_and_no_confirmed_trade_date':
      return 'Neither the balance nor the movements carry a date to compare.'
    case 'suggested_trade_contradicts_object_sign_rule':
      return 'Recording the difference as a movement would leave the record in a state it is not allowed to hold, so a missing movement cannot be the explanation.'
    case 'restated_balance_contradicts_object_sign_rule':
      return "Restating the balance to the movements' sum would leave the record in a state it is not allowed to hold, so an out-of-date balance cannot be the whole explanation — the movement history itself is incomplete."
    case 'not_a_balance_versus_trades_break':
      return 'This break compares two stored figures, not a balance against movements.'
    default:
      return 'The cause could not be determined from the dates on file.'
  }
}

export default function ReconcilePane({
  task,
  onResolved,
}: {
  task: TaskQueueItem
  onResolved: () => void
}) {
  const data = (task.task_data ?? {}) as ReconTaskData
  const kind = (data.target_kind ?? task.target_type ?? 'position') as 'position' | 'instrument'
  const diagnosis: BreakDiagnosis | undefined = data.diagnosis
  const cause = diagnosis?.cause ?? 'undeterminable'
  const canResolve = isResolvableBreak(task.task_type)

  const [drawer, setDrawer] = useState<DrawerLite | null>(null)
  const [reason, setReason] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  // Role honesty (Tier-1 C1): both verbs render per the rank the backend
  // enforces TODAY (resolve-break / confirm-pre-existing = any ops
  // officer); a role below it sees the verb disabled with the rule.
  const role = getCurrentUser()?.role ?? null
  const breakRule = verbRule('reco_resolve_break', role)
  const openingRule = verbRule('reco_confirm_pre_existing', role)
  // The officer's call. Pre-selected to the suggestion when the engine had
  // one — never when it didn't (an undeterminable break must not nudge).
  const [choice, setChoice] = useState<'missing_trade' | 'stale_balance' | null>(
    cause === 'missing_trade' || cause === 'stale_balance' ? cause : null
  )
  const [deltaOverride, setDeltaOverride] = useState<string>('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const objectId = data.target_id ?? task.target_id

  useEffect(() => {
    if (!task.fund_id || !objectId) return
    apiFetch<DrawerLite>(`/api/funds/${task.fund_id}/objects/${kind}/${objectId}`)
      .then(setDrawer)
      .catch(() => {})
  }, [task.fund_id, kind, objectId])

  // The numbers come from the DIAGNOSIS — the backend computed both sides
  // at detection time. The object drawer is a nicety for the title now; it
  // used to be load-bearing, and when its fetch failed the pane showed "—"
  // against "—" with no explanation.
  const storedFromDrawer =
    num(drawer?.facts['units held']) ?? num(drawer?.facts['outstanding units'])
  const delta = num(diagnosis?.delta) ?? num(data.expected_units_delta)
  const stored = num(diagnosis?.stored) ?? storedFromDrawer
  // Tri-quantity Option B stores a committed facility as TWO position rows —
  // the commitment leg and its undrawn mirror, which is NEGATIVE by
  // construction (ck_positions_units_held_role_sign). Labelling the mirror
  // "units held" made the card read as a negative holding, which no
  // depositary can attest to.
  //
  // The first attempt keyed this off the object drawer's `role` fact, which
  // silently did nothing: the drawer fetch is fund-scoped, and the packs
  // where undrawn legs actually surface are precisely the ones whose tasks
  // carry no fund yet — so the fetch never ran and every card fell back to
  // "units held" (atlas p3, 2026-07-28). The sign is on the diagnosis the
  // card already has, so read it there and depend on nothing.
  const positionRole = String(drawer?.facts['role'] ?? '').toLowerCase()
  const balanceLabel =
    kind !== 'position'
      ? 'outstanding units'
      : positionRole === 'undrawn' || (stored != null && stored < 0)
        ? 'undrawn balance'
        : positionRole === 'commitment'
          ? 'committed units'
          : 'units held'
  const tradesSum =
    num(diagnosis?.computed) ?? (stored != null && delta != null ? stored - delta : null)

  const balanceDate = diagnosis?.balance_as_of ?? data.balance_update_date ?? null
  const tradeDate = diagnosis?.latest_trade_date ?? data.latest_trade_effective_date ?? null
  const suggestedDelta = diagnosis?.suggested_units_delta ?? data.expected_units_delta ?? null
  const isOverride = cause !== 'undeterminable' && choice !== null && choice !== cause
  // Restating to the movements' sum would break the record's own sign rule
  // (§1032 commitment-family legs, a non-negative supply) — the backend
  // refuses it with a 409, so the pane doesn't offer it. The movement-side
  // call remains, and with it the obligation that chases the document.
  const restateBarred = diagnosis?.reason === 'restated_balance_contradicts_object_sign_rule'

  async function confirmPreExisting() {
    setBusy(true)
    setError(null)
    try {
      await apiFetch(`/api/tasks/${task.task_id}/confirm-pre-existing`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      })
      setDone(
        'Inception balance booked — the balance and movements now agree. Any open document request for this task was cancelled.'
      )
    } catch (e) {
      setError(errText(e))
    } finally {
      setBusy(false)
    }
  }

  async function resolveBreak() {
    if (!choice) return
    setBusy(true)
    setError(null)
    try {
      await apiFetch(`/api/tasks/${task.task_id}/resolve-break`, {
        method: 'POST',
        body: JSON.stringify({
          outcome: choice,
          units_delta: choice === 'missing_trade' && deltaOverride ? deltaOverride : undefined,
          note: note || undefined,
        }),
      })
      setDone(
        choice === 'missing_trade'
          ? `Movement of ${deltaOverride || fmt(delta)} recorded under your name — the balance now ties out. A corroborating document is still owed: it is in the queue, and the record is not fully verified until it arrives.`
          : `Balance restated to ${fmt(tradesSum)} under your name — the record and its movements now agree. A corroborating document is still owed: it is in the queue, and the record is not fully verified until it arrives.`
      )
    } catch (e) {
      setError(errText(e))
    } finally {
      setBusy(false)
    }
  }

  if (done)
    return (
      <div className="vcard" style={{ flex: 1, alignSelf: 'flex-start', padding: 22 }}>
        <div
          data-testid="recon-done"
          style={{ fontSize: 'var(--text-13)', color: 'var(--verified)', fontWeight: 600 }}
        >
          ✓ {done}
        </div>
        <button className="vbtn pri" style={{ marginTop: 16 }} onClick={onResolved}>
          Next task →
        </button>
      </div>
    )

  // Never title the card with a raw id (atlas review 2026-07-27): when the
  // drawer lookup is unavailable, fall back to the task title's object tail
  // ("Position recon (trade missing): Fund — Instrument" → the part after
  // the colon), which the backend already writes in officer vocabulary.
  const titleTail = task.title?.split(/:\s(.+)/)[1]
  const label = drawer?.title ?? titleTail ?? `this ${kind}`

  return (
    <div style={{ flex: 1, display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      {/* r-ledger-compare + the diagnosis */}
      <div className="vcard" style={{ flex: 1, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ fontSize: 'var(--text-13)', fontWeight: 700 }}>
            {label} <HelpDot k="pane.reconcile" />
          </div>
          <div style={{ fontSize: 'var(--text-12)', color: 'var(--faint)', marginTop: 2 }}>
            the record's balance and its movements disagree — every number on a record must be
            explained by movements
          </div>
        </div>
        <div style={{ display: 'flex' }}>
          <div style={{ flex: 1, padding: '14px 18px', borderRight: '1px solid var(--line)' }}>
            <Sentence
              as="p"
              data-testid="recon-stored"
              meaning="recon.record_side"
              style={{ margin: 0, fontSize: 'var(--text-12)' }}
              values={{ stored, unit_word: balanceLabel, balance_date: balanceDate }}
            />
          </div>
          <div style={{ flex: 1, padding: '14px 18px' }}>
            <Sentence
              as="p"
              data-testid="recon-computed"
              meaning="recon.movements_side"
              style={{ margin: 0, fontSize: 'var(--text-12)' }}
              values={{ computed: tradesSum, latest_trade: tradeDate }}
            />
          </div>
        </div>

        {/* The diagnosis, with the dates that justify it — the officer must
            be able to CHECK the reasoning, not just trust it. */}
        <div
          data-testid="recon-diagnosis"
          data-cause={cause}
          style={{
            padding: '12px 18px',
            borderTop: '1px solid var(--line)',
            background: '#fdf6f3',
            fontSize: 'var(--text-12)',
          }}
        >
          <span className="mono" style={{ fontWeight: 700, color: 'var(--gap)' }}>
            {fmt(delta)}
          </span>{' '}
          {unitWord(delta)} unexplained.{' '}
          {cause === 'missing_trade' && (
            <>
              The balance was evidenced on <b>{balanceDate}</b>, later than the most recent movement
              (<b>{tradeDate}</b>) — so the balance is the current one and{' '}
              <b>a movement of {fmt(num(suggestedDelta))} is missing</b>.
            </>
          )}
          {cause === 'stale_balance' && (
            <>
              The most recent movement (<b>{tradeDate}</b>) is later than the balance evidence (
              <b>{balanceDate}</b>) — so the movements are the current picture and{' '}
              <b>the stored balance is out of date</b>.
            </>
          )}
          {cause === 'undeterminable' && (
            <>
              {undeterminableBecause(diagnosis?.reason ?? '', balanceDate)}{' '}
              <b>Which side is right is your call.</b>
            </>
          )}
        </div>
      </div>

      {/* the officer's call */}
      <div style={{ width: 380, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {error && (
          <div
            className="vcard"
            data-testid="recon-error"
            style={{ padding: '10px 14px', fontSize: 'var(--text-12)', color: 'var(--gap)' }}
          >
            {error}
          </div>
        )}

        {!canResolve ? (
          <div className="vcard" style={{ padding: 16 }} data-testid="recon-not-resolvable">
            <div style={{ fontSize: 'var(--text-13)', fontWeight: 700 }}>
              Two stored figures disagree
            </div>
            <div style={{ fontSize: 'var(--text-12)', color: 'var(--muted)', marginTop: 4 }}>
              This break compares two figures the record already holds, not a balance against
              movements — so there is no missing-movement or stale-balance call to make. It clears
              itself once the underlying figures agree again. Open the fund to see what is driving
              them apart.
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <Link className="vbtn" to="/upload">
                Upload statement
              </Link>
              <Link className="vbtn" to={`/v2/funds/${task.fund_id}`}>
                Open fund →
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="vcard" style={{ padding: 16 }} data-testid="recon-decision">
              <div style={{ fontSize: 'var(--text-13)', fontWeight: 700 }}>
                Which side is right?
              </div>
              <div style={{ fontSize: 'var(--text-12)', color: 'var(--muted)', marginTop: 4 }}>
                {cause === 'undeterminable'
                  ? 'The dates on file do not settle it. Your call changes the record, under your name.'
                  : 'The suggestion below follows from the dates. Change it if you know better — your call is what gets written.'}
              </div>
              <div style={{ fontSize: 'var(--text-12)', color: 'var(--muted)', marginTop: 6 }}>
                Either way a corroborating document is still owed, and it will appear in the queue:
                the record is not fully verified until it arrives and is linked to the object.
              </div>

              <label
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'flex-start',
                  fontSize: 'var(--text-12)',
                  marginTop: 12,
                }}
              >
                <input
                  type="radio"
                  data-testid="choose-missing-trade"
                  checked={choice === 'missing_trade'}
                  onChange={() => setChoice('missing_trade')}
                  style={{ marginTop: 2 }}
                />
                <span>
                  <b>A movement is missing.</b>{' '}
                  <span style={{ color: 'var(--muted)' }}>
                    The balance is right; a movement of {fmt(num(suggestedDelta))} was never
                    recorded. Confirming writes it.
                  </span>
                </span>
              </label>

              <label
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'flex-start',
                  fontSize: 'var(--text-12)',
                  marginTop: 10,
                }}
              >
                <input
                  type="radio"
                  data-testid="choose-stale-balance"
                  checked={choice === 'stale_balance'}
                  onChange={() => setChoice('stale_balance')}
                  disabled={restateBarred}
                  style={{ marginTop: 2 }}
                />
                <span style={restateBarred ? { opacity: 0.55 } : undefined}>
                  <b>The balance is out of date.</b>{' '}
                  <span style={{ color: 'var(--muted)' }}>
                    {restateBarred ? (
                      <>
                        Not available here: the movements sum to {fmt(tradesSum)}, which is not a
                        balance this record is allowed to hold — the movement history itself is
                        incomplete.
                      </>
                    ) : (
                      <>
                        The movements are right; the stored figure has not caught up. Confirming
                        restates the balance to {fmt(tradesSum)}.
                      </>
                    )}
                  </span>
                </span>
              </label>

              {choice === 'missing_trade' && (
                <input
                  data-testid="recon-delta-override"
                  placeholder={`movement size (default ${fmt(num(suggestedDelta))})`}
                  value={deltaOverride}
                  onChange={(e) => setDeltaOverride(e.target.value)}
                  style={{
                    width: '100%',
                    marginTop: 12,
                    font: 'inherit',
                    fontSize: 'var(--text-12)',
                    padding: '7px 10px',
                    border: '1px solid var(--line)',
                    borderRadius: 7,
                  }}
                />
              )}

              {isOverride && (
                <div
                  data-testid="recon-override-notice"
                  style={{
                    fontSize: 'var(--text-12)',
                    color: 'var(--gap)',
                    marginTop: 10,
                    fontWeight: 600,
                  }}
                >
                  This overrides the suggestion — say why, so the next reader understands.
                </div>
              )}

              <input
                data-testid="recon-note"
                placeholder={isOverride ? 'why (required to override)' : 'note (optional)'}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                style={{
                  width: '100%',
                  marginTop: 10,
                  font: 'inherit',
                  fontSize: 'var(--text-12)',
                  padding: '7px 10px',
                  border: '1px solid var(--line)',
                  borderRadius: 7,
                }}
              />

              <button
                className="vbtn pri"
                data-testid="recon-confirm"
                style={{ marginTop: 12 }}
                onClick={resolveBreak}
                title={breakRule.rule ?? undefined}
                disabled={
                  busy || !breakRule.allowed || choice === null || (isOverride && !note.trim())
                }
              >
                {choice === 'stale_balance' ? 'Restate the balance' : 'Record the movement'}
              </button>
              {!breakRule.allowed ? (
                <div className="cn-rule" style={{ marginTop: 6 }} data-testid="recon-verb-rule">
                  {breakRule.rule}
                </div>
              ) : null}
            </div>

            {/* Chase single-source (Ale ruling 2026-08-09): the break does
                NOT mint its own document request. Settling it books the
                entry, and the M1 movement obligation on that entry is what
                chases the paper — one lineage, not two. */}
            {task.task_type.endsWith('_trade_missing') && (
              <div className="vcard" style={{ padding: 16 }}>
                {/* Copy pass (glossary 2026-07-24): the Bible citation means
                    nothing to a client officer — plain line, §914a kept as a
                    hover detail for Ale-side debugging. */}
                <div style={{ fontSize: 'var(--text-13)', fontWeight: 700 }} title="Bible §914a">
                  The position pre-dates this mandate
                </div>
                <div style={{ fontSize: 'var(--text-12)', color: 'var(--muted)', marginTop: 4 }}>
                  {/* A NEGATIVE delta is the undrawn mirror of a commitment, not a
                      negative holding. "records a deemed opening position of
                      -25,000,000 units, under your name" is a sentence no
                      depositary head will sign (atlas p8 d6/d8, 2026-07-29):
                      correct by construction, unsignable as written. Say what
                      the number IS. Also softened "no document will EVER
                      exist" — nobody can attest to that about the future. */}
                  {(num(delta) ?? 0) < 0 ? (
                    <>
                      This is the undrawn side of a commitment — the headroom not yet drawn, which
                      the record carries as a negative mirror of the commitment leg. Confirming
                      opens it at {fmt(Math.abs(num(delta) ?? 0))}{' '}
                      {unitWord(Math.abs(num(delta) ?? 0))} undrawn, entered in the audit trail
                      under your name.
                    </>
                  ) : (
                    <>
                      It pre-dates the mandate, so no document is expected for it. Confirming
                      records an opening position of {fmt(delta)} {unitWord(delta)} — entered in the
                      audit trail under your name.
                    </>
                  )}
                </div>
                {!confirmOpen ? (
                  <>
                    <button
                      className="vbtn"
                      style={{ marginTop: 12 }}
                      onClick={() => setConfirmOpen(true)}
                      disabled={!openingRule.allowed}
                      title={openingRule.rule ?? undefined}
                      data-testid="recon-open-opening"
                    >
                      Book inception balance
                    </button>
                    {!openingRule.allowed ? (
                      <div
                        className="cn-rule"
                        style={{ marginTop: 6 }}
                        data-testid="recon-opening-rule"
                      >
                        {openingRule.rule}
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div style={{ marginTop: 12 }}>
                    <input
                      autoFocus
                      placeholder="why does the position pre-date the fund? (required)"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      style={{
                        width: '100%',
                        font: 'inherit',
                        fontSize: 'var(--text-12)',
                        padding: '7px 10px',
                        border: '1.5px solid var(--line)',
                        borderRadius: 7,
                      }}
                    />
                    <button
                      className="vbtn pri"
                      style={{ marginTop: 10 }}
                      onClick={confirmPreExisting}
                      disabled={busy || !reason.trim()}
                    >
                      Record the opening position
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
